<template>
  <div class="Cart">
    <button class="Close" @click="cartState.showCart.value = false">
      <div class="CloseHoverArea">
        <ModalClose class="CloseIcon" />
      </div>
    </button>
    <div class="CartInner">
      <div class="head" ref="headElementRef">
        <div class="deliveryInfo">
          <DeliveryLimit
            :delivery-limit-date-label="deliveryLimitDateLabel"
            :delivery-limit-time-label="deliveryLimitTimeLabel"
          />
          <div class="deliveryFee">注文金額 4,000円以上で送料0円（当社負担）</div>
        </div>

        <CartSummary
          :totalOrderNum="totalOrderNum"
          :merchandise-amount="merchandiseAmount"
        />
      </div>

      <div class="body">
        <div
          v-if="hasCartItems"
          class="CartItems"
          :style="{height: cartItemsHeight}"
        >
          <template v-for="item in cartState.pokemonCart.value.items" :key="item.product.inventory_product_id + item.rank">
            <div class="CartItem">
              <PokemonSingleItem
                v-if="item.productType === 'single'"
                :item="item"
                @numAdd="cartStore.incementeSingleProductFromPokemonCart(item)"
                @numSubtract="decrementSingleProductFromCart('pokemon', item)"
                @leaveCart="cartState.showCart.value = false"
              />
              <template v-if="!!normalizedAvailabilityValidationResultsByInventoryId[item.inventoryId]?.length">
                <div class="availabilityValidationResult" v-for="message in normalizedAvailabilityValidationResultsByInventoryId[item.inventoryId]" :key="message">
                  <ToastWarning/>
                  <div class="availabilityValidationResultMessage">{{ message }}</div>
                </div>
              </template>
            </div>
          </template>
          <div class="clearAllButton">
            <TextButton text="すべて削除" @click="showClearAllModal = true"/>
          </div>
        </div>
        <div v-else class="empty">
          <CartEmpty class="emptyIcon" />
          <div>現在、カートに商品はありません。</div>
        </div>
      </div>

      <div class="foot" ref="footElementRef">
        <OrderPriceValidationAlert
          v-if="isBelowMinimumOrderPrice"
        />
        <div class="footActions">
          <template v-if="isSignedIn">
            <Button
              text="購入へ進む"
              platform="sell"
              @click="checkout('order')"
              :disabled="isBelowMinimumOrderPrice || isValidatingAvailability"
              :loading="isValidatingAvailability"
            />
          </template>
          <template v-else>
            <Button
              text="新規会員登録して購入"
              platform="sell"
              @click="checkout('signup')"
              :disabled="isBelowMinimumOrderPrice || isValidatingAvailability"
            />
            <Button
              text="ログインして購入"
              platform="sell"
              mode="secondary"
              @click="checkout('signin')"
              :disabled="isBelowMinimumOrderPrice || isValidatingAvailability"
            />
          </template>
        </div>
      </div>
    </div>

    <Modal :show="showClearAllModal" @close="showClearAllModal = false">
      <template v-slot:body>カート内の商品をすべて削除します。</template>
      <template v-slot:footer>
        <Button
          text="キャンセル"
          mode="secondary"
          style="height: 42px; width: 100%"
          @click="showClearAllModal = false"
        />
        <Button
          text="削除"
          mode="distructive"
          style="height: 42px; width: 100%"
          @click="clearAll"
        />
      </template>
    </Modal>

    <AvailabilityValidationResult
      mode="cart"
      :results="normalizedAvailabilityValidationResults"
      :show="showAvailabilityValidationResultModal"
      @close="showAvailabilityValidationResultModal = false"
    />
  </div>
</template>

<script lang="ts">
import { defineNuxtComponent, useNuxtApp, useRouter } from "#imports";
import { computed, ref, onMounted } from "vue";
import { DITokens } from "@/di";
import { storeToRefs } from "pinia";
import type { Genre } from '@/types/genre'
import type { PokemonCartItem } from '@/types/cart'
import { ModalClose, CartEmpty, ToastWarning } from "@ka-nabellinc/ka-nabell-icons";
import { Button, Modal, TextLink, TextButton } from "@ka-nabellinc/ka-nabell-ui";
import { useGTM } from '@/composables/useGTM'
import DeliveryLimit from "@/components/Order/DeliveryLimit";
import PokemonSingleItem from "@/components/Cart/Item/Pokemon/Single";
import CartSummary from '@/components/Cart/Summary'
import AvailabilityValidationResult from '@/components/Cart/AvailabilityValidation/Result'
import OrderPriceValidationAlert from "@/components/Cart/OrderPriceValidation/Alert"
import { useCart } from "./useCart";

export default defineNuxtComponent({
  components: {
    ModalClose,
    CartEmpty,
    ToastWarning,
    Modal,
    Button,
    DeliveryLimit,
    PokemonSingleItem,
    CartSummary,
    TextLink,
    AvailabilityValidationResult,
    OrderPriceValidationAlert,
    TextButton,
  },
  setup() {
    const showClearAllModal = ref(false)
    const cartItemsHeight = ref<string>()
    const headElementRef = ref<HTMLDivElement>()
    const footElementRef = ref<HTMLDivElement>()
    const { $container } = useNuxtApp();
    const router = useRouter();
    const profileStore = $container.resolve(DITokens.ProfileStore);
    const profileState = storeToRefs(profileStore);
    const cartStore = $container.resolve(DITokens.CartStore);
    const cartState = storeToRefs(cartStore);
    const orderStore = $container.resolve(DITokens.OrderStore)
    const { deliveryLimitTimeLabel, deliveryLimitDateLabel } = storeToRefs(orderStore)
    const { sendGTMEvent } = useGTM($container)

    const {
      checkout,
      isValidatingAvailability,
      showAvailabilityValidationResultModal,
      normalizedAvailabilityValidationResultsByInventoryId,
      normalizedAvailabilityValidationResults
    } = useCart($container);

    const genre = computed(() => router.currentRoute.value.meta.genre);
    const hasCartItems = computed(() => {
      if (genre.value === 'pokemon') {
        return !!cartState.pokemonCart.value.items.length
      } else {
        false
      }
    })
    const totalOrderNum = computed(() => {
      if (genre.value === "pokemon") {
        return cartState.pokemonCartTotalOrderNum.value;
      }
      return 0;
    });
    const merchandiseAmount = computed(() => {
      if (genre.value === "pokemon") {
        return cartState.pokemonCartMerchandiseAmount.value;
      }
      return 0;
    });

    const decrementSingleProductFromCart = (genre: Genre, item: PokemonCartItem) => {
      if (genre === 'pokemon' && item.productType === 'single' && item.product.genre === 'POKEMON') {
        cartStore.decrementSingleProductFromPokemonCart(item)
      }

      // GTM Event: https://docs.google.com/spreadsheets/d/1ZLvgxU1CnwcEaohiMpFKnL2A8C75_4j5I1Ol7Tzb2fU/edit?gid=906310070#gid=906310070&range=B25
      sendGTMEvent({
        event: 'delete_from_cart',
        ecommerce: {
          currency: 'JPY',
          value: item.unitPrice,
          items: [
            {
              item_id: item.inventoryId,
              item_name: item.product.product_name,
              item_category: genre === 'pokemon' ? 'ポケモンカード' : 'ポケモンカード',
              price: item.unitPrice,
              quantity: 1
            }
          ]
        },
      })
    }
    const clearAll = () => {
      // GTM Event: https://docs.google.com/spreadsheets/d/1ZLvgxU1CnwcEaohiMpFKnL2A8C75_4j5I1Ol7Tzb2fU/edit?gid=906310070#gid=906310070&range=B25
      let items: GTMSingleItemType[] = []

      if (genre.value === 'pokemon') {
        for (const item of cartStore.pokemonCart.items) {
          items.push({
            item_id: item.inventoryId,
            item_name: item.product.product_name,
            item_category: 'ポケモンカード',
            price: item.unitPrice,
            quantity: item.orderNum
          })
        }

        cartStore.clearPokemonCart()
      }
      showClearAllModal.value = false

      sendGTMEvent({
        event: 'delete_from_cart',
        ecommerce: {
          currency: 'JPY',
          value: items.reduce((acc, ac) => acc + (ac.price * ac.quantity), 0),
          items
        }
      })
    }

    const isBelowMinimumOrderPrice = computed(
      () => merchandiseAmount.value < 2000
    );

    onMounted(() => {
      if (process.client) {
        if (!headElementRef.value || !footElementRef.value) {
          return
        }

        const PageMergin = 48
        const Gap = 20
        const viewHeight = document.documentElement.clientHeight;
        const headHeight = headElementRef.value.clientHeight + Gap
        const footHeight = footElementRef.value.clientHeight + Gap

        const cartItemsHeightNum = viewHeight - (headHeight + footHeight + PageMergin)
  
        cartItemsHeight.value = `${cartItemsHeightNum}px`
      }
    })

    return {
      genre,
      cartStore,
      cartState,
      isSignedIn: profileState.isSignedIn,
      checkout,
      totalOrderNum,
      merchandiseAmount,
      isBelowMinimumOrderPrice,
      hasCartItems,
      showClearAllModal,
      showAvailabilityValidationResultModal,
      normalizedAvailabilityValidationResults,
      normalizedAvailabilityValidationResultsByInventoryId,
      decrementSingleProductFromCart,
      clearAll,
      isValidatingAvailability,
      cartItemsHeight,
      headElementRef,
      footElementRef,

      deliveryLimitTimeLabel,
      deliveryLimitDateLabel,
    };
  },
});
</script>

<style scoped>
.Cart {
  background-color: #fff;
  padding: 28px 20px 20px 20px;
  width: 100%;
  height: 100%;
  margin: 0;
  position: relative;
}

@media screen and (min-width: 430px) {
  .Cart {
    width: 430px;
    margin: 0 auto;
  }
}

.Close {
  width: 80px;
  height: 60px;
  top: 3px;
  right: 3px;
  position: absolute;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: .3s;
  z-index: 1;
  -webkit-tap-highlight-color:rgba(0,0,0,0);
}

.CloseHoverArea {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  position: relative;
  left: 15px;
  top: -5px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.Close:hover .CloseHoverArea {
  background-color: var(--semantic-colors-background-gray);
}

.CloseIcon {
  width: 24px;
  height: 24px;
}

.CartInner {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

.head {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
}

.deliveryInfo {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.deliveryFee {
  font-size: 12px;
  line-height: 150%;
  font-weight: 700;
  letter-spacing: 0.15px;
}

.body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  font-size: 14px;
  line-height: 150%;
  letter-spacing: 0.14px;
  color: var(--semantic-colors-text-gray);
  position: relative;
  top: 140px;
}

.clearAllButton {
  width: 100%;
  display: flex;
  justify-content: flex-end;
}

.foot {
  background-color: #fff;
  padding-top: 20px;
  position: absolute;
  left: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.footActions {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.CartItems {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.CartItems::-webkit-scrollbar{
  display: none;
}

.CartItem {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.availabilityValidationResult {
  font-size: 13px;
  line-height: 150%;
  letter-spacing: 0.13px;
  display: flex;
  align-items: center;
  gap: 4px;
  color: #723D0C;
}
</style>
